/**
 * BarChart Component
 * Reusable bar chart with horizontal and stacking support
 */

"use client";

import { ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  Bar,
  CartesianGrid,
  LabelList,
  Legend,
  BarChart as RechartsBarChart,
  XAxis,
  YAxis,
} from "recharts";
import { ChartWrapper } from "./ChartWrapper";
import { BarChartProps, BaseChartData } from "./types";
import { formatAxisTick } from "./utils";

export const BarChart = ({
  data,
  config,
  dataKeys,
  xAxisKey,
  title,
  description,
  footer,
  className,
  containerClassName,
  height,
  stacked = false,
  horizontal = false,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  showTooltip = true,
  showLegend = false,
  tooltipConfig,
  legendConfig,
  barSize,
  radius = 8,
  showLabels = false,
  labelConfig,
  accessibilityLayer = true,
  metricValue,
  metricChange,
  showMetricLayout = false,
  headerActions,
  xAxisFormatter,
  yAxisFormatter,
}: BarChartProps) => {
  // Wrapper functions for formatAxisTick to match Recharts signature
  const formatXAxisTick = (value: any) =>
    xAxisFormatter ? xAxisFormatter(value) : formatAxisTick(value);
  const formatYAxisTick = (value: any) =>
    yAxisFormatter ? yAxisFormatter(value) : formatAxisTick(value, true);
  console.log("formatXAxisTick:", formatXAxisTick(xAxisKey));
  console.log("formatYAxisTick:", formatYAxisTick(xAxisKey));

  console.log("XAxisKey:", xAxisKey);

  const chartContent = (
    <RechartsBarChart
      accessibilityLayer={accessibilityLayer}
      data={data}
      layout={horizontal ? "vertical" : "horizontal"}
      margin={{ top: 10, right: 12, left: 12, bottom: 0 }}
    >
      {showGrid && <CartesianGrid vertical={false} />}

      {!horizontal && showXAxis && (
        <XAxis
          dataKey={xAxisKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatXAxisTick}
          className="text-xs text-muted-foreground"
        />
      )}
      {!horizontal && showYAxis && (
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatYAxisTick}
          className="text-xs text-muted-foreground"
        />
      )}

      {horizontal && showXAxis && (
        <XAxis
          type="number"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatYAxisTick}
          className="text-xs text-muted-foreground"
        />
      )}
      {horizontal && showYAxis && (
        <YAxis
          type="category"
          dataKey={xAxisKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={formatXAxisTick}
          className="text-xs text-muted-foreground"
        />
      )}

      {showTooltip && (
        <ChartTooltip
          cursor={tooltipConfig?.cursor ?? false}
          content={
            <ChartTooltipContent
              hideLabel={tooltipConfig?.hideLabel}
              nameKey={tooltipConfig?.nameKey}
            />
          }
          defaultIndex={tooltipConfig?.defaultIndex}
        />
      )}

      {showLegend && (
        <Legend
          verticalAlign={legendConfig?.position || "top"}
          height={36}
          iconType={legendConfig?.iconType || "rect"}
        />
      )}

      {dataKeys.map((key: keyof BaseChartData, index: number) => {
        const configKey = String(key);

        // Use color from config, fallback to dynamic chart color if not defined
        const configColor = config[configKey]?.color;
        const fallbackChartNumber = (index % 5) + 1;
        const chartVariable =
          configColor || `var(--chart-${fallbackChartNumber})`;

        return (
          <Bar
            key={configKey}
            dataKey={key}
            stackId={stacked ? "stack" : undefined}
            fill={chartVariable}
            radius={radius}
            maxBarSize={barSize}
          >
            {showLabels && (
              <LabelList
                position={labelConfig?.position || "top"}
                offset={labelConfig?.offset || 12}
                className={labelConfig?.className || "fill-foreground"}
                fontSize={labelConfig?.fontSize || 12}
              />
            )}
          </Bar>
        );
      })}
    </RechartsBarChart>
  );

  return (
    <ChartWrapper
      title={title}
      description={description}
      footer={footer}
      className={className}
      containerClassName={containerClassName}
      config={config}
      height={height}
      metricValue={metricValue}
      metricChange={metricChange}
      showMetricLayout={showMetricLayout}
      headerActions={headerActions}
    >
      {chartContent}
    </ChartWrapper>
  );
};
BarChart.displayName = "BarChart";

export default BarChart;
